import React, { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Bookmark, Plus } from "lucide-react";
import {
  useGetWishlistQuery,
  useAddToWishlistMutation,
  useRemoveFromWishlistMutation,
} from "../../redux/api/wishlistApi";

/**
 * Changes vs previous version:
 *
 * 1. Quick View removed — no modal, no ProductQuickView import,
 *    no Eye button, no quickOpen state. Card click → PDP directly.
 *
 * 2. GSAP removed — was firing a ScrollTrigger animation on every
 *    card instance (one GSAP context per card). That's expensive
 *    when there are 8+ cards in a Swiper. The Swiper container
 *    already fades in via Framer Motion in Home.jsx.
 *
 * 3. Blur placeholder blink fixed — same technique as ProductDetailPage:
 *    check img.complete synchronously after mount. Blur placeholder
 *    removed entirely (it caused the double-blink by racing onLoad).
 *    Simple bg-stone-100 skeleton instead.
 *
 * 4. Image carousel scroll listener kept — progress bar is useful UX.
 *
 * 5. role="button" on a div replaced with a proper <article> wrapper
 *    and <button> for the card click — better semantics and a11y.
 */

const cloudUrl = (url, w = 600) =>
  url?.replace("/upload/", `/upload/f_auto,q_auto,w_${w}/`);

const ProductCard = React.memo(({ product, addItemToCart, layout }) => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const imgRefs = useRef([]);

  const { data } = useGetWishlistQuery(undefined, { skip: !user });
  const wishlist = data?.wishlist || [];
  const wishlistItem = useMemo(
    () => wishlist.find((item) => item.product?._id === product._id),
    [wishlist, product._id]
  );
  const isInWishlist = Boolean(wishlistItem);

  const [addToWishlist] = useAddToWishlistMutation();
  const [removeFromWishlist] = useRemoveFromWishlistMutation();

  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState({});

  // Check synchronously for cached images — prevents blink on revisit
  useEffect(() => {
    setLoaded((prev) => {
      const next = { ...prev };
      imgRefs.current.forEach((el, i) => {
        if (el?.complete && el.naturalWidth > 0) next[i] = true;
      });
      return next;
    });
  }, [product.images]);

  // Scroll progress for multi-image strip
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max ? (el.scrollLeft / max) * 100 : 0);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleWishlist = async (e) => {
    e?.stopPropagation?.();
    if (!user) return navigate("/login");
    try {
      if (isInWishlist) await removeFromWishlist(wishlistItem._id).unwrap();
      else await addToWishlist(product._id).unwrap();
    } catch {}
  };

  const addToCart = (e) => {
    e?.stopPropagation?.();
    if (product.stock > 0) addItemToCart(product);
  };

  if (!product) return null;

  const imgWidth = layout === "list" ? 1200 : 600;

  return (
    <article className="group relative flex flex-col overflow-hidden  hover:shadow-md transition-shadow duration-300">

      {/* Clickable image area → PDP */}
      <div
        className="cursor-pointer"
        onClick={() => navigate(`/products/${product._id}`)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/products/${product._id}`);
          }
        }}
        aria-label={`View ${product.name}`}
      >
        {/* Image carousel */}
        <div className="relative w-full aspect-[4/5] overflow-hidden  ">
          <div
            ref={containerRef}
            className="flex h-full overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide"
          >
            {product.images.map((img, idx) => (
              <div
                key={idx}
                className="relative flex-shrink-0 w-full h-full snap-center"
              >
                {/* Skeleton only while loading */}
                {!loaded[idx] && (
                  <div className="absolute inset-0 bg-stone-200 animate-pulse rounded-xl" />
                )}
                <img
                  ref={(el) => (imgRefs.current[idx] = el)}
                  src={cloudUrl(img.url, imgWidth)}
                  alt={`${product.name}${product.images.length > 1 ? ` — view ${idx + 1}` : ""}`}
                  className={`w-full h-full object-cover transition-opacity duration-300
                    ${loaded[idx] ? "opacity-100" : "opacity-0"}`}
                  loading={idx === 0 ? "eager" : "lazy"}
                  fetchPriority={idx === 0 ? "high" : "auto"}
                  draggable={false}
                  onLoad={() => setLoaded((p) => ({ ...p, [idx]: true }))}
                />
              </div>
            ))}
          </div>

          {/* Scroll progress bar */}
          {product.images.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black/10">
              <div
                className="h-full bg-stone-600 transition-[width] duration-150 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Add to cart */}
          <button
            onClick={addToCart}
            disabled={product.stock <= 0}
            className="absolute bottom-3 left-3 z-20 p-2.5 rounded-full bg-white/85 text-stone-800 shadow-sm hover:bg-stone-900 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={`Add ${product.name} to bag`}
          >
            <Plus className="w-4 h-4" />
          </button>

          {/* Wishlist */}
          <button
            onClick={toggleWishlist}
            className="absolute bottom-3 right-3 z-20 p-2.5 rounded-full bg-white/85 text-stone-700 shadow-sm hover:text-[#3fbcb1] hover:bg-white transition"
            aria-pressed={isInWishlist}
            aria-label={isInWishlist ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
          >
            <Bookmark
              className="w-4 h-4"
              fill={isInWishlist ? "#3fbcb1" : "none"}
              stroke={isInWishlist ? "#3fbcb1" : "currentColor"}
            />
          </button>
        </div>

        {/* Caption */}
        <div className="px-1 pt-3 pb-2 text-center">
          <h3
            className="text-sm md:text-base font-medium text-stone-800 line-clamp-2 font-sourcesans"
            style={{ textWrap: "balance" }}
          >
            {product.name}
          </h3>
          {product.price != null && (
            <p className="text-sm md:text-base text-stone-600 mt-0.5 font-sourcesans">
              ${Number(product.price).toFixed(2)}
            </p>
          )}
        </div>
      </div>
    </article>
  );
});

ProductCard.displayName = "ProductCard";
export default ProductCard;